import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { ArrowLeft, RefreshCw, Eye, Info, X } from 'lucide-react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import AffiliateCommissionLedger from './AffiliateCommissionLedger';
import { getAffiliateLinksByUserId, updateAffiliateLinkStatus } from '../../Services/AffiliateInfo/AffiliateApi';
import GlobalPagination from '../../Components/Common/Globalpagination';

// Status styling configuration
const STATUS_STYLES = {
    active: { bg: '#E8F5E8', color: '#2E7D32', border: '#C8E6C9' },
    inactive: { bg: '#F1F5F9', color: '#64748B', border: '#E2E8F0' },
    suspended: { bg: '#FFFBEB', color: '#D97706', border: '#FDE68A' },
    deleted: { bg: '#FEF2F2', color: '#991B1B', border: '#FECACA' }
};

const DEFAULT_STATUS_STYLE = { bg: '#F1F5F9', color: '#64748B', border: '#E2E8F0' };

const getStatusStyle = (status) => STATUS_STYLES[(status || '').toLowerCase()] || DEFAULT_STATUS_STYLE;

const statusMenuProps = {
    PaperProps: {
        sx: {
            boxShadow: 'none',
            border: '1px solid #E2E8F0',
            borderRadius: '6px',
            mt: 0.5,
            p: 0,
            overflow: 'hidden',
            '& .MuiList-root': {
                py: 0.5,
            },
            '& .MuiMenuItem-root': {
                fontSize: '0.75rem',
                fontWeight: 500,
                py: 0.75,
                px: 1.25,
                color: '#1E293B',
                '&.Mui-selected': {
                    bgcolor: '#FFFDF9',
                    color: '#D97706',
                    fontWeight: 600,
                    '&:hover': {
                        bgcolor: '#FFF8EC'
                    }
                },
                '&:hover': {
                    bgcolor: '#F8FAFC'
                }
            }
        }
    }
};

// Date & time helper for audit metadata
const formatDateTime = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return String(dateString);
    return date.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
    });
};

// Currency helper
const formatCurrency = (amount) => {
    const num = Number(amount) || 0;
    return `₹${num.toLocaleString('en-IN')}`;
};

const AffiliateLinksClicks = ({ onBack, selectedAffiliate: propAffiliate }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();

    // Resolve affiliate from props, router state, or URL params — no hardcoded fallback ID
    const passedAffiliate = propAffiliate || location.state?.affiliate || null;
    const urlUserId = searchParams.get('userId') || searchParams.get('affiliateUserId') || searchParams.get('id');

    const userId =
        passedAffiliate?.affiliateUserId ||
        passedAffiliate?.userId ||
        passedAffiliate?.id ||
        passedAffiliate?._id ||
        passedAffiliate?.raw?.affiliateUserId ||
        passedAffiliate?.raw?.userId ||
        passedAffiliate?.raw?.id ||
        passedAffiliate?.raw?._id ||
        passedAffiliate?.internalId ||
        location.state?.userId ||
        location.state?.id ||
        urlUserId;

    // Data states
    const [linksList, setLinksList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [updatingLinkId, setUpdatingLinkId] = useState(null);
    const [error, setError] = useState(null);

    // Audit Dialog state
    const [auditDialogOpen, setAuditDialogOpen] = useState(false);
    const [selectedLinkAudit, setSelectedLinkAudit] = useState(null);

    // Pagination state (API-driven)
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [pagination, setPagination] = useState({
        totalItems: 0,
        totalPages: 1,
        currentPage: 1,
        limit: 10,
        hasNextPage: false,
        hasPrevPage: false
    });
    const [summary, setSummary] = useState(null);

    // View state: 'links' | 'ledger'
    const [currentView, setCurrentView] = useState('links');
    const [selectedLinkForLedger, setSelectedLinkForLedger] = useState(null);

    // Resolve affiliate display info
    const affiliateDisplayName = passedAffiliate?.name || passedAffiliate?.affiliateName || passedAffiliate?.raw?.name || passedAffiliate?.raw?.affiliateName || '';
    const affiliateDefaultCode = passedAffiliate?.code || passedAffiliate?.affiliateCode || passedAffiliate?.raw?.code || passedAffiliate?.raw?.affiliateCode || '';

    // Map raw API item to UI model
    const getLinkData = useCallback((item) => {
        if (!item) return null;
        const id = item.id || item.linkId;
        const affiliateName = item.affiliateName || item.user?.name || item.name || affiliateDisplayName || '—';
        const affiliateCode = item.affiliateCode || item.code || affiliateDefaultCode;
        const refCode = item.refCode || (affiliateCode ? `?ref=${affiliateCode}` : '—');
        const targetPage = item.targetPage || item.targetUrl || '—';
        const clicks = Number(item.clicks || 0);
        const bookings = Number(item.bookings || 0);
        const cvr = item.cvr || (clicks > 0 ? `${((bookings / clicks) * 100).toFixed(2)}%` : '0%');
        const earned = Number(item.earned || item.earnings || item.commission || 0);
        const status = (item.status || 'active').toLowerCase();

        return {
            id,
            affiliateName,
            affiliateCode,
            refCode,
            targetPage,
            clicks,
            bookings,
            cvr,
            earned,
            status,
            cuName: item.cuName || '—',
            cuId: item.cuId ?? '—',
            cuType: item.cuType || '—',
            createdAt: item.createdAt || null,
            uuName: item.uuName || '—',
            uuId: item.uuId ?? '—',
            uuType: item.uuType || '—',
            updatedAt: item.updatedAt || null,
            raw: item
        };
    }, [affiliateDisplayName, affiliateDefaultCode]);

    // Fetch links directly from backend API
    const fetchLinks = useCallback(async (currentPage = 1, currentLimit = limit) => {
        if (!userId) {
            setError('No affiliate user selected. Please select an affiliate from the list.');
            setLoading(false);
            setLinksList([]);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const params = { page: currentPage };
            if (currentLimit !== 10) {
                params.limit = currentLimit;
            }
            const response = await getAffiliateLinksByUserId(userId, params);

            const links = response?.links || response?.data || [];
            const paginationData = response?.pagination || {
                totalItems: links.length,
                totalPages: 1,
                currentPage: currentPage,
                limit: currentLimit,
                hasNextPage: false,
                hasPrevPage: false
            };

            setLinksList(links);
            setPagination(paginationData);
            setSummary(response?.summary || null);
        } catch (err) {
            console.error('Error fetching affiliate links:', err);
            setError(err.response?.data?.message || err.message || 'Failed to load affiliate links.');
            setLinksList([]);
        } finally {
            setLoading(false);
        }
    }, [userId, limit]);

    useEffect(() => {
        fetchLinks(page, limit);
    }, [page, limit, fetchLinks]);

    // Processed links and summary values
    const processedLinks = linksList.map(getLinkData).filter(Boolean);
    const activeCount = summary?.activeCount ?? processedLinks.filter(l => l.status === 'active').length;
    const totalEarned = summary?.totalEarned ?? processedLinks.reduce((sum, l) => sum + (l.earned || 0), 0);

    // Optimistic status update with rollback
    const handleStatusUpdate = async (item, newStatus) => {
        const linkId = item.id;
        if (!linkId) return;

        const previousStatus = item.status;

        // Optimistic UI update
        setLinksList(prev => prev.map(row => {
            const rId = row.id || row.linkId;
            return rId === linkId ? { ...row, status: newStatus } : row;
        }));

        setUpdatingLinkId(linkId);
        setError(null);

        try {
            await updateAffiliateLinkStatus(linkId, newStatus);
        } catch (err) {
            console.error('Error updating link status:', err);
            setError(err.response?.data?.message || err.message || 'Failed to update link status.');
            // Rollback on failure
            setLinksList(prev => prev.map(row => {
                const rId = row.id || row.linkId;
                return rId === linkId ? { ...row, status: previousStatus } : row;
            }));
        } finally {
            setUpdatingLinkId(null);
        }
    };

    const handleBack = () => {
        if (onBack) {
            onBack();
        } else {
            navigate('/affiliates');
        }
    };

    const handleViewLedger = (link) => {
        setSelectedLinkForLedger(link);
        setCurrentView('ledger');
    };

    const handleOpenAudit = (item) => {
        setSelectedLinkAudit(item);
        setAuditDialogOpen(true);
    };

    const handleCloseAudit = () => {
        setAuditDialogOpen(false);
        setSelectedLinkAudit(null);
    };

    if (currentView === 'ledger') {
        return (
            <AffiliateCommissionLedger
                selectedAffiliate={passedAffiliate}
                selectedLink={selectedLinkForLedger}
                onBack={() => {
                    setCurrentView('links');
                    setSelectedLinkForLedger(null);
                }}
            />
        );
    }

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
            {/* Top Back Navigation & Header */}
            <Stack spacing={2} sx={{ mb: 3 }}>
                <Box>
                    <Button
                        startIcon={<ArrowLeft size={16} />}
                        onClick={handleBack}
                        disableElevation
                        disableFocusRipple
                        disableRipple
                        disableTouchRipple
                        sx={{
                            color: '#64748B',
                            textTransform: 'none',
                            fontSize: '0.82rem',
                            fontWeight: 500,
                            p: 0,
                            mb: 1.5,
                            minWidth: 'auto',
                            '&:hover': {
                                bgcolor: 'transparent',
                                color: '#1E293B'
                            }
                        }}
                    >
                        Back to Affiliates
                    </Button>

                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        spacing={1.5}
                    >
                        <Box>
                            <Typography
                                sx={{
                                    color: '#1E293B',
                                    fontWeight: 600,
                                    fontSize: { xs: '1.5rem', md: '1.75rem' },
                                    lineHeight: 1.2
                                }}
                            >
                                Links & Clicks
                            </Typography>
                            <Typography sx={{ color: '#64748B', fontSize: '0.82rem', mt: 0.5 }}>
                                {loading ? 'Loading links...' : `${activeCount} active · ${formatCurrency(totalEarned)} earned${affiliateDisplayName ? ` · Filtered for: ${affiliateDisplayName}` : ''}`}
                            </Typography>
                        </Box>

                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => fetchLinks(page, limit)}
                            startIcon={<RefreshCw size={14} />}
                            disabled={loading || !userId}
                            sx={{
                                color: '#64748B',
                                textTransform: 'none',
                                fontSize: '0.8rem',
                                fontWeight: 500,
                                height: '36px',
                                px: 1.5,
                                borderRadius: '6px',
                                border: '1px solid #E2E8F0',
                                bgcolor: '#FFFFFF',
                                boxShadow: 'none',
                                '&:hover': {
                                    bgcolor: '#F8FAFC',
                                    borderColor: '#CBD5E1',
                                    color: '#1E293B',
                                    boxShadow: 'none'
                                }
                            }}
                        >
                            Refresh
                        </Button>
                    </Stack>
                </Box>

                {/* Link Tracking Info Banner */}
                <Paper
                    elevation={0}
                    sx={{
                        p: 1.75,
                        px: 2,
                        borderRadius: '6px',
                        border: '1px solid #E8E8E8',
                        borderLeft: '4px solid #803927',
                        bgcolor: '#FFFFFF',
                        boxShadow: 'none'
                    }}
                >
                    <Typography sx={{ fontSize: '0.82rem', color: '#475569', lineHeight: 1.5 }}>
                        <strong style={{ color: '#1E293B', fontWeight: 600 }}>Link tracking:</strong> 90-day first-party cookie, last click wins. Admin can see every link, disable abusive ones, and spot self-dealing.
                    </Typography>
                </Paper>
            </Stack>

            {/* Error Alert */}
            {error && (
                <Alert severity="error" sx={{ mb: 2.5, borderRadius: '6px', fontSize: '0.82rem', boxShadow: 'none' }}>
                    {error}
                </Alert>
            )}

            {/* Links & Clicks Table */}
            <Paper
                elevation={0}
                sx={{
                    borderRadius: '8px',
                    border: '1px solid #E8E8E8',
                    bgcolor: '#FFFFFF',
                    overflow: 'hidden',
                    boxShadow: 'none'
                }}
            >
                <TableContainer>
                    <Table sx={{ minWidth: 950 }}>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#F8FAFC', borderBottom: '2px solid #EEF2F6' }}>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, width: '18%' }}>
                                    Affiliate
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.25, width: '15%' }}>
                                    Link / Ref Code
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.25, width: '18%' }}>
                                    Target Page
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1, textAlign: 'center', width: '8%' }}>
                                    Clicks
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1, textAlign: 'center', width: '8%' }}>
                                    Bookings
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1, textAlign: 'center', width: '8%' }}>
                                    CVR
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.25, width: '11%' }}>
                                    Earned
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1, textAlign: 'center', width: '14%' }}>
                                    Status
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.25, textAlign: 'right', width: '8%' }}>
                                    Actions
                                </TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                                        <CircularProgress size={24} sx={{ color: '#FFBD65' }} />
                                    </TableCell>
                                </TableRow>
                            ) : processedLinks.length > 0 ? (
                                processedLinks.map((item) => {
                                    const statusStyle = getStatusStyle(item.status);
                                    return (
                                        <TableRow
                                            key={item.id}
                                            sx={{
                                                '& td': { borderBottom: '1px solid #F0F0F0' },
                                                '&:last-child td': { borderBottom: 0 },
                                            }}
                                        >
                                            {/* Affiliate Name */}
                                            <TableCell sx={{ py: 1.35, px: 1.5 }}>
                                                <Typography sx={{ fontSize: '0.78rem', fontWeight: 500, color: '#1E293B' }}>
                                                    {item.affiliateName}
                                                </Typography>
                                            </TableCell>

                                            {/* Link / Ref Code */}
                                            <TableCell sx={{ py: 1.35, px: 1.25 }}>
                                                <Box
                                                    sx={{
                                                        display: 'inline-block',
                                                        px: 0.75,
                                                        py: 0.2,
                                                        borderRadius: '4px',
                                                        bgcolor: '#F1F5F9',
                                                        color: '#334155',
                                                        fontSize: '0.74rem',
                                                        fontWeight: 500,
                                                        fontFamily: 'monospace'
                                                    }}
                                                >
                                                    {item.refCode}
                                                </Box>
                                            </TableCell>

                                            {/* Target Page */}
                                            <TableCell sx={{ py: 1.35, px: 1.25 }}>
                                                <Typography
                                                    sx={{
                                                        fontSize: '0.75rem',
                                                        color: '#475569',
                                                        fontFamily: 'monospace',
                                                        display: 'inline-block'
                                                    }}
                                                >
                                                    {item.targetPage}
                                                </Typography>
                                            </TableCell>

                                            {/* Clicks */}
                                            <TableCell sx={{ py: 1.35, px: 1, textAlign: 'center', fontSize: '0.76rem', color: '#334155', fontWeight: 500 }}>
                                                {item.clicks.toLocaleString('en-IN')}
                                            </TableCell>

                                            {/* Bookings */}
                                            <TableCell sx={{ py: 1.35, px: 1, textAlign: 'center', fontSize: '0.76rem', color: '#334155', fontWeight: 500 }}>
                                                {item.bookings}
                                            </TableCell>

                                            {/* CVR */}
                                            <TableCell sx={{ py: 1.35, px: 1, textAlign: 'center', fontSize: '0.76rem', color: '#475569', fontWeight: 500 }}>
                                                {item.cvr}
                                            </TableCell>

                                            {/* Earned */}
                                            <TableCell sx={{ py: 1.35, px: 1.25, fontSize: '0.76rem', color: item.earned > 0 ? '#1E293B' : '#64748B', fontWeight: item.earned > 0 ? 600 : 400, whiteSpace: 'nowrap' }}>
                                                {formatCurrency(item.earned)}
                                            </TableCell>

                                            {/* Status Dropdown */}
                                            <TableCell sx={{ py: 1, px: 1, textAlign: 'center', whiteSpace: 'nowrap' }}>
                                                {updatingLinkId === item.id ? (
                                                    <Box sx={{ display: 'inline-flex', justifyContent: 'center', alignItems: 'center', height: '26px', width: '105px' }}>
                                                        <CircularProgress size={16} sx={{ color: '#FFBD65' }} />
                                                    </Box>
                                                ) : (
                                                    <FormControl size="small" sx={{ minWidth: 105 }}>
                                                        <Select
                                                            value={item.status}
                                                            onChange={(e) => handleStatusUpdate(item, e.target.value)}
                                                            MenuProps={statusMenuProps}
                                                            sx={{
                                                                height: '26px',
                                                                fontSize: '0.72rem',
                                                                fontWeight: 500,
                                                                textTransform: 'lowercase',
                                                                borderRadius: '4px',
                                                                bgcolor: statusStyle.bg,
                                                                color: statusStyle.color,
                                                                '& .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: statusStyle.border,
                                                                },
                                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: '#CBD5E1',
                                                                },
                                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: '#FFBD65',
                                                                    borderWidth: '1px',
                                                                },
                                                                '& .MuiSelect-select': {
                                                                    py: '2px !important',
                                                                    px: '8px !important',
                                                                },
                                                                '& .MuiSvgIcon-root': {
                                                                    fontSize: '1rem',
                                                                    color: statusStyle.color,
                                                                }
                                                            }}
                                                        >
                                                            <MenuItem value="active" sx={{ fontSize: '0.75rem' }}>active</MenuItem>
                                                            <MenuItem value="inactive" sx={{ fontSize: '0.75rem' }}>inactive</MenuItem>
                                                            <MenuItem value="suspended" sx={{ fontSize: '0.75rem' }}>suspended</MenuItem>
                                                            <MenuItem value="deleted" sx={{ fontSize: '0.75rem' }}>deleted</MenuItem>
                                                            {!['active', 'inactive', 'suspended', 'deleted'].includes(item.status) && item.status && (
                                                                <MenuItem value={item.status} sx={{ display: 'none' }}>{item.status}</MenuItem>
                                                            )}
                                                        </Select>
                                                    </FormControl>
                                                )}
                                            </TableCell>

                                            {/* Actions */}
                                            <TableCell align="right" sx={{ py: 1.1, px: 1.25, whiteSpace: 'nowrap' }}>
                                                <Stack direction="row" spacing={0.75} justifyContent="flex-end" alignItems="center">
                                                    <Tooltip title="View Commission" arrow placement="top">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleViewLedger(item)}
                                                            sx={{
                                                                color: '#334155',
                                                                border: '1px solid #CBD5E1',
                                                                borderRadius: '4px',
                                                                p: 0.6,
                                                                height: '28px',
                                                                width: '28px',
                                                                bgcolor: '#FFFFFF',
                                                                boxShadow: 'none',
                                                                '&:hover': {
                                                                    bgcolor: '#F8FAFC',
                                                                    borderColor: '#94A3B8',
                                                                    color: '#1E293B',
                                                                    boxShadow: 'none'
                                                                }
                                                            }}
                                                        >
                                                            <Eye size={15} />
                                                        </IconButton>
                                                    </Tooltip>

                                                    <Tooltip title="Audit Details" arrow placement="top">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleOpenAudit(item)}
                                                            sx={{
                                                                color: '#803927',
                                                                border: '1px solid #E2E8F0',
                                                                borderRadius: '4px',
                                                                p: 0.6,
                                                                height: '28px',
                                                                width: '28px',
                                                                bgcolor: '#FFFFFF',
                                                                boxShadow: 'none',
                                                                '&:hover': {
                                                                    bgcolor: '#FFF8EC',
                                                                    borderColor: '#FFBD65',
                                                                    color: '#3D1F10',
                                                                    boxShadow: 'none'
                                                                }
                                                            }}
                                                        >
                                                            <Info size={15} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={9} align="center" sx={{ py: 6, color: '#64748B', fontSize: '0.78rem' }}>
                                        No links found
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Pagination Controls */}
            <GlobalPagination
                page={page}
                pageSize={limit}
                totalRecords={pagination.totalItems || processedLinks.length}
                totalPages={pagination.totalPages}
                onPageChange={(newPage) => {
                    setPage(newPage);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                onPageSizeChange={(newLimit) => {
                    setLimit(newLimit);
                    setPage(1);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                loading={loading}
            />

            {/* Audit Details Modal Dialog */}
            <Dialog
                open={auditDialogOpen}
                onClose={handleCloseAudit}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '8px',
                        border: '1px solid #E2E8F0',
                        boxShadow: 'none',
                        p: 0,
                        overflow: 'hidden'
                    }
                }}
            >
                <DialogTitle
                    sx={{
                        py: 2,
                        px: 2.5,
                        borderBottom: '1px solid #EEF2F6',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        bgcolor: '#FFFFFF'
                    }}
                >
                    <Box>
                        <Typography sx={{ fontSize: '1.05rem', fontWeight: 600, color: '#1E293B' }}>
                            Link Audit & Creator Details
                        </Typography>
                        <Typography sx={{ fontSize: '0.78rem', color: '#64748B', mt: 0.25 }}>
                            {selectedLinkAudit?.refCode ? `Ref: ${selectedLinkAudit.refCode}` : 'Audit metadata for affiliate link'}
                            {selectedLinkAudit?.affiliateName ? ` · ${selectedLinkAudit.affiliateName}` : ''}
                        </Typography>
                    </Box>
                    <IconButton
                        size="small"
                        onClick={handleCloseAudit}
                        sx={{
                            color: '#64748B',
                            p: 0.5,
                            '&:hover': { bgcolor: '#F1F5F9', color: '#1E293B' }
                        }}
                    >
                        <X size={18} />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ p: 2.5, bgcolor: '#FBFBFB' }}>
                    <Stack spacing={2} sx={{ mt: 0.5 }}>
                        {/* Link Overview Card */}
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                borderRadius: '6px',
                                border: '1px solid #E8E8E8',
                                bgcolor: '#FFFFFF',
                                boxShadow: 'none'
                            }}
                        >
                            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#1E293B', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1.5 }}>
                                Link Overview
                            </Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                                <Box>
                                    <Typography sx={{ fontSize: '0.7rem', color: '#64748B', fontWeight: 500 }}>Target Page</Typography>
                                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#334155', mt: 0.2, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                        {selectedLinkAudit?.targetPage || '—'}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography sx={{ fontSize: '0.7rem', color: '#64748B', fontWeight: 500 }}>Referral Code</Typography>
                                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#1E293B', mt: 0.2, fontFamily: 'monospace' }}>
                                        {selectedLinkAudit?.refCode || '—'}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography sx={{ fontSize: '0.7rem', color: '#64748B', fontWeight: 500 }}>Performance</Typography>
                                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#334155', mt: 0.2 }}>
                                        {selectedLinkAudit?.clicks || 0} clicks · {selectedLinkAudit?.bookings || 0} bookings ({selectedLinkAudit?.cvr || '0%'} CVR)
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography sx={{ fontSize: '0.7rem', color: '#64748B', fontWeight: 500 }}>Total Earned</Typography>
                                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#2E7D32', mt: 0.2 }}>
                                        {formatCurrency(selectedLinkAudit?.earned || 0)}
                                    </Typography>
                                </Box>
                            </Box>
                        </Paper>

                        {/* Created By Section */}
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                borderRadius: '6px',
                                border: '1px solid #E8E8E8',
                                bgcolor: '#FFFFFF',
                                boxShadow: 'none'
                            }}
                        >
                            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#2E7D32', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1.5 }}>
                                Created By Metadata (CU)
                            </Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                                <Box>
                                    <Typography sx={{ fontSize: '0.7rem', color: '#64748B', fontWeight: 500 }}>User Name (cuName)</Typography>
                                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#1E293B', mt: 0.2 }}>
                                        {selectedLinkAudit?.cuName || '—'}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography sx={{ fontSize: '0.7rem', color: '#64748B', fontWeight: 500 }}>User ID (cuId)</Typography>
                                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#1E293B', mt: 0.2 }}>
                                        {selectedLinkAudit?.cuId ?? '—'}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography sx={{ fontSize: '0.7rem', color: '#64748B', fontWeight: 500 }}>User Type (cuType)</Typography>
                                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 500, color: '#334155', mt: 0.2, textTransform: 'capitalize' }}>
                                        {selectedLinkAudit?.cuType || '—'}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography sx={{ fontSize: '0.7rem', color: '#64748B', fontWeight: 500 }}>Created At</Typography>
                                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#334155', mt: 0.2 }}>
                                        {formatDateTime(selectedLinkAudit?.createdAt)}
                                    </Typography>
                                </Box>
                            </Box>
                        </Paper>

                        {/* Updated By Section */}
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                borderRadius: '6px',
                                border: '1px solid #E8E8E8',
                                bgcolor: '#FFFFFF',
                                boxShadow: 'none'
                            }}
                        >
                            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#D97706', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1.5 }}>
                                Updated By Metadata (UU)
                            </Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                                <Box>
                                    <Typography sx={{ fontSize: '0.7rem', color: '#64748B', fontWeight: 500 }}>User Name (uuName)</Typography>
                                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#1E293B', mt: 0.2 }}>
                                        {selectedLinkAudit?.uuName || '—'}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography sx={{ fontSize: '0.7rem', color: '#64748B', fontWeight: 500 }}>User ID (uuId)</Typography>
                                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#1E293B', mt: 0.2 }}>
                                        {selectedLinkAudit?.uuId ?? '—'}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography sx={{ fontSize: '0.7rem', color: '#64748B', fontWeight: 500 }}>User Type (uuType)</Typography>
                                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 500, color: '#334155', mt: 0.2, textTransform: 'capitalize' }}>
                                        {selectedLinkAudit?.uuType || '—'}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography sx={{ fontSize: '0.7rem', color: '#64748B', fontWeight: 500 }}>Updated At</Typography>
                                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#334155', mt: 0.2 }}>
                                        {formatDateTime(selectedLinkAudit?.updatedAt)}
                                    </Typography>
                                </Box>
                            </Box>
                        </Paper>
                    </Stack>
                </DialogContent>

                <DialogActions sx={{ p: 2, px: 2.5, borderTop: '1px solid #EEF2F6', bgcolor: '#FFFFFF' }}>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={handleCloseAudit}
                        sx={{
                            color: '#64748B',
                            borderColor: '#CBD5E1',
                            textTransform: 'none',
                            fontSize: '0.8rem',
                            fontWeight: 500,
                            borderRadius: '6px',
                            height: '32px',
                            px: 2,
                            boxShadow: 'none',
                            '&:hover': {
                                bgcolor: '#F8FAFC',
                                borderColor: '#94A3B8',
                                color: '#1E293B',
                                boxShadow: 'none'
                            }
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AffiliateLinksClicks;
